import { Image as ImageLayer } from 'ol/layer'
import ImageWMS from 'ol/source/ImageWMS'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useMap from 'hooks/use-map'
import { isGisProxyEnabled, wmsEndpointUrl } from 'common/geoserver'
import type { AppState } from 'state/index.js'

import {
  type AbortingImageLoader,
  createAbortingImageLoader,
} from './aborting-image-loader'
import { CLUSTER_MAX_ZOOM, buildViewparams, gridZoomFor } from './grid-zoom'

/**
 * How long to wait after the last resolution change before showing the clustered layer
 * again. Long enough to cover a wheel or pinch gesture's run of intermediate resolutions,
 * short enough not to read as the layer having disappeared.
 */
const ZOOM_SETTLE_MS = 150

type Props = {
  layerName?: string
  gridLayerName?: string
  mapRef?: string
  tenantId: string
  layerZIndex?: number
}

type SourceHandle = {
  source: ImageWMS
  loader: AbortingImageLoader
}

/**
 * The tenant/year/zoom scope of a request: a SQL view `viewparams` for
 * GeoServer, or YEAR and Z for the GIS proxy, which takes the tenant from its
 * session.
 */
function scopeParams(
  tenantId: string,
  year: number | undefined,
  gridZoom?: number,
): Record<string, string> {
  if (!isGisProxyEnabled()) {
    return { viewparams: buildViewparams(tenantId, year, gridZoom) }
  }
  return {
    YEAR: String(year ?? ''),
    ...(gridZoom === undefined ? {} : { Z: String(gridZoom) }),
  }
}

function createSource(
  layers: string,
  scope: Record<string, string>,
  label: string,
): SourceHandle {
  const loader = createAbortingImageLoader(label)
  const source = new ImageWMS({
    url: wmsEndpointUrl('outside-points'),
    serverType: 'geoserver',
    // Default is 1.5, which requests 1.5x the viewport in each direction - 2.25x the area,
    // and so 2.25x the points - for margin we never pan far enough to use.
    ratio: 1,
    // Default is true, which on a 2x display doubles WIDTH/HEIGHT and appends
    // FORMAT_OPTIONS=dpi:180. That quadruples the raster and, because GeoServer derives
    // the scale denominator from BBOX and WIDTH, would shift the CLUSTER_MAX_ZOOM handoff
    // by a zoom level on retina displays but not elsewhere.
    hidpi: false,
    imageLoadFunction: loader,
    params: {
      TRANSPARENT: true,
      LAYERS: layers,
      ...scope,
    },
  })
  return { source, loader }
}

// Points outside all field boundaries belong to no harvest and no field, so unlike
// MapWMSLayer this is scoped by tenant + year only (no harvest_id/client_id). Retention
// is current-calendar-year only on the backend, so any other year is a guaranteed-empty
// request - gate on that here rather than issue it.
export default function MapOutsideBoundaryPointsLayer(props: Props): null {
  const {
    layerName = OUTSIDE_BOUNDARY_POINTS_LAYER_NAME,
    gridLayerName = OUTSIDE_BOUNDARY_POINTS_GRID_LAYER_NAME,
    mapRef = 'map',
    tenantId,
    layerZIndex = 5,
  } = props

  const map = useMap(mapRef)

  const visible = useSelector(
    (state: AppState) => state.map.outsideBoundaryPointsVisible,
  )
  const opacity = useSelector(
    (state: AppState) => state.map.outsideBoundaryPointsOpacity,
  )
  const selectedYear = useSelector((state: AppState) => state.field.year)
  const isCurrentYear = selectedYear === new Date().getFullYear()

  // Truthiness, not `!== ''`: an unset build-time layer name arrives as the literal
  // `undefined` rather than an empty string, which slipped past the old check and sent a
  // GetMap with no LAYERS at all. A missing layer name means this feature is not
  // configured here, so the right behaviour is to render nothing.
  const enabled =
    Boolean(tenantId) &&
    visible &&
    isCurrentYear &&
    Boolean(layerName) &&
    Boolean(gridLayerName)

  const raw = useMemo(
    () =>
      enabled
        ? createSource(
            layerName,
            scopeParams(tenantId, selectedYear),
            'outside-boundary points',
          )
        : undefined,
    [enabled, layerName, tenantId, selectedYear],
  )
  const rawSource = raw?.source

  // Cancels whatever GetMap `raw` still has in flight the moment it stops being the active
  // source - on tenant/year change or the feature toggling off - rather than leaving it to
  // ride out REQUEST_TIMEOUT_MS against a browser connection and GeoServer capacity that the
  // now-current source needs.
  useEffect(() => {
    return () => raw?.loader.cancel()
  }, [raw])

  // Deliberately not keyed on the current zoom: recreating the source on every zoom step
  // would throw away the image ol is still showing. The z viewparam is pushed in below.
  const grid = useMemo(
    () =>
      enabled
        ? createSource(
            gridLayerName,
            scopeParams(tenantId, selectedYear, CLUSTER_MAX_ZOOM),
            'outside-boundary point clusters',
          )
        : undefined,
    [enabled, gridLayerName, tenantId, selectedYear],
  )
  const gridSource = grid?.source

  useEffect(() => {
    return () => grid?.loader.cancel()
  }, [grid])

  // minZoom/maxZoom on the layers (below) only hide the inactive layer - they don't touch
  // its source, which stays mounted with whatever GetMap it last issued. Without this, that
  // request rides out REQUEST_TIMEOUT_MS on a browser connection and GeoServer capacity the
  // now-active source needs, exactly like the tenant/year-change case above, just triggered
  // by crossing CLUSTER_MAX_ZOOM instead of a prop change.
  useEffect(() => {
    if (!map || !raw || !grid) return

    const view = map.getView()
    let rawActive = (view.getZoom() ?? 0) > CLUSTER_MAX_ZOOM

    const handleHandoff = () => {
      const zoom = view.getZoom()
      if (zoom === undefined) return

      const nextRawActive = zoom > CLUSTER_MAX_ZOOM
      if (nextRawActive === rawActive) return
      rawActive = nextRawActive

      // Cancel the request the now-inactive source has in flight, then refresh() - which
      // ImageWMS overrides to drop its cached image - so a later handoff back into this
      // source's range asks GeoServer again instead of re-showing the blank image the
      // cancellation settled it to.
      const superseded = nextRawActive ? grid : raw
      superseded.loader.cancel()
      superseded.source.refresh()
    }

    view.on('change:resolution', handleHandoff)

    return () => {
      view.un('change:resolution', handleHandoff)
    }
  }, [map, raw, grid])

  // The grid is precomputed per zoom level, so the request has to say which level it
  // wants. Tracked on moveend rather than change:resolution: the latter fires once per
  // animation frame of a zoom, and each distinct value would be its own GetMap.
  useEffect(() => {
    if (!map || !gridSource) return

    const view = map.getView()
    // moveend fires after pans too, not just zooms, so re-derive the level every time but
    // only push params - and trigger another GetMap - when it actually changed.
    let currentGridZoom = gridZoomFor(view.getZoom())

    gridSource.updateParams(
      scopeParams(tenantId, selectedYear, currentGridZoom),
    )

    const syncGridZoom = () => {
      const nextGridZoom = gridZoomFor(view.getZoom())
      if (nextGridZoom === currentGridZoom) return

      currentGridZoom = nextGridZoom
      gridSource.updateParams(scopeParams(tenantId, selectedYear, nextGridZoom))
    }

    map.on('moveend', syncGridZoom)

    return () => {
      map.un('moveend', syncGridZoom)
    }
  }, [map, gridSource, tenantId, selectedYear])

  // Deliberately no setExtent on either layer, unlike MapStaticBoundaryLayer: off-field
  // points can legitimately fall outside the tenant's default field extent (road transit,
  // a neighbouring parcel) - clipping them is exactly the failure this layer exists to
  // surface.
  //
  // ol visibility is `zoom > minZoom && zoom <= maxZoom` with fractional zoom, so pairing
  // minZoom on the raw layer with maxZoom on the grid layer meets exactly at
  // CLUSTER_MAX_ZOOM: no gap, no overlap, including mid-animation.
  const rawLayer = useMemo(
    () =>
      rawSource === undefined
        ? undefined
        : new ImageLayer({
            source: rawSource,
            zIndex: layerZIndex,
            minZoom: CLUSTER_MAX_ZOOM,
            opacity,
          }),
    // opacity deliberately excluded - applied via setOpacity below so a slider drag doesn't
    // recreate the layer (and drop its in-flight GetMap) on every step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawSource, layerZIndex],
  )

  const gridLayer = useMemo(
    () =>
      gridSource === undefined
        ? undefined
        : new ImageLayer({
            source: gridSource,
            zIndex: layerZIndex,
            maxZoom: CLUSTER_MAX_ZOOM,
            opacity,
          }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridSource, layerZIndex],
  )

  useEffect(() => {
    rawLayer?.setOpacity(opacity)
    gridLayer?.setOpacity(opacity)
  }, [rawLayer, gridLayer, opacity])

  useEffect(() => {
    if (!map || !rawLayer) return

    map.addLayer(rawLayer)
    return () => {
      map.removeLayer(rawLayer)
    }
  }, [map, rawLayer])

  useEffect(() => {
    if (!map || !gridLayer) return

    map.addLayer(gridLayer)

    // Hide the clustered layer while the zoom is animating. Cluster membership genuinely
    // differs between adjacent zoom levels, so the symbols jump when the new image lands;
    // this waits for the zoom to settle instead of showing the intermediate state. The
    // raw-point layer does not cluster and so does not need it.
    const view = map.getView()
    let currentResolution = view.getResolution()
    let zoomTimeout: ReturnType<typeof setTimeout> | null = null

    const handleResolutionChange = () => {
      const newResolution = view.getResolution()

      if (newResolution !== currentResolution) {
        gridLayer.setVisible(false)

        if (zoomTimeout) {
          clearTimeout(zoomTimeout)
        }

        zoomTimeout = setTimeout(() => {
          gridLayer.setVisible(true)
          currentResolution = newResolution
        }, ZOOM_SETTLE_MS)
      }
    }

    view.on('change:resolution', handleResolutionChange)

    return () => {
      if (zoomTimeout) {
        clearTimeout(zoomTimeout)
      }
      view.un('change:resolution', handleResolutionChange)
      map.removeLayer(gridLayer)
    }
  }, [map, gridLayer])

  return null
}
